/**
 * SetupCardForClientButton
 * ------------------------
 * Coach-side card-on-file management.
 *
 *   - Shows current status: "✓ Visa •••• 4242" / "No card on file"
 *   - "Open here" → opens Stripe Checkout (mode=setup) in a new tab, so the
 *     coach can enter the parent's card while they're on the phone or at
 *     the gym counter.
 *   - "Email link" → emails the parent a one-tap Stripe-hosted card-setup
 *     link (no charge).
 *
 * Both flows POST to `/coach/clients/{client_user_id}/setup-card` with
 * `delivery: open|email`. PCI SAQ-A preserved (card never touches our DOM).
 *
 * Props:
 *   - clientUserId : parent's user_id
 *   - clientEmail  : parent's email (for the "email link" button)
 *   - clientName   : optional display label
 *   - onChanged    : optional callback when card-on-file state changes
 */
import { useCallback, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Loader2, RefreshCw, Search, X } from "lucide-react";
import CardSelector from "@/components/coach/CardSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


/**
 * DiagnosticPanel
 * ---------------
 * Renders the response of GET /coach/clients/{id}/stripe-customer-diagnostic.
 * Lists every Stripe customer matching the client's email (and parent's),
 * with attached card brand/last4, default_payment_method, and a flag for
 * which one is canonical in our DB. Helps the owner understand WHY a card
 * isn't showing despite multiple "Add card" attempts by the parent.
 */
function DiagnosticPanel({ data, userId, onClose, onChanged }) {
    const customers = data?.stripe_customers || [];
    const summary = data?.summary || {};
    const [linking, setLinking] = useState(null);

    const forceLink = async (cid) => {
        if (!userId) return;
        const cust = customers.find((c) => c.stripe_customer_id === cid);
        const cardLine = cust?.attached_card_count > 0
            ? `${cust.attached_card_count} card(s) on this customer.`
            : "⚠ This customer has NO cards attached.";
        if (!window.confirm(
            `Force-link this MaxSC user to Stripe customer ${cid}?\n\n${cardLine}\n\nThis OVERRIDES the current link. The user's "stripe_customer_id" will now point at this customer for all future charges, auto-renewals, and subscriptions.`,
        )) return;
        setLinking(cid);
        try {
            const r = await axios.post(
                `${API}/coach/clients/${userId}/link-stripe-customer/${cid}`,
                {}, { withCredentials: true },
            );
            toast.success(
                r.data?.card_on_file
                    ? "✓ Linked. Card-on-file is now live — you can charge them."
                    : "Linked, but no card detected on this customer. They'll still need to add one.",
                { duration: 8000 },
            );
            onChanged?.();
            onClose();
        } catch (e) {
            toast.error("Force-link failed", { description: e?.response?.data?.detail || "Server error" });
        } finally { setLinking(null); }
    };

    return (
        <div
            data-testid="card-diagnostic-panel"
            className="mt-3 border border-amber-700/40 bg-zinc-950 p-3"
        >
            <div className="flex items-center justify-between mb-2.5">
                <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-amber-200">
                    Stripe customer diagnostic
                </div>
                <button
                    type="button"
                    data-testid="card-diagnostic-close"
                    onClick={onClose}
                    className="text-zinc-500 hover:text-white"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
            <div className="text-[10px] text-zinc-400 mb-2 leading-snug">
                Searched: <span className="font-mono text-zinc-300">{(data.emails_searched || []).join(", ") || "(no email on file)"}</span>
                {data.parent && (
                    <> · Parent: <span className="font-mono text-zinc-300">{data.parent.email || data.parent.name || data.parent.user_id}</span></>
                )}
            </div>
            <div className="text-[10px] mb-3 grid grid-cols-3 gap-2 border border-zinc-800 p-2">
                <div>
                    <div className="text-zinc-500 tracking-[0.15em] uppercase">Customers</div>
                    <div className="text-white text-base font-mono">{summary.total_customers_found ?? 0}</div>
                </div>
                <div>
                    <div className="text-zinc-500 tracking-[0.15em] uppercase">With cards</div>
                    <div className={`text-base font-mono ${(summary.customers_with_cards || 0) > 0 ? "text-emerald-300" : "text-red-300"}`}>
                        {summary.customers_with_cards ?? 0}
                    </div>
                </div>
                <div>
                    <div className="text-zinc-500 tracking-[0.15em] uppercase">Total cards</div>
                    <div className="text-white text-base font-mono">{summary.total_attached_cards ?? 0}</div>
                </div>
            </div>
            {customers.length === 0 ? (
                <div className="text-xs text-red-300 border border-red-700/40 bg-red-950/20 p-2.5 leading-snug">
                    No Stripe customers exist for any of these email addresses. The parent&apos;s &ldquo;Add card&rdquo; clicks must have failed before reaching Stripe Checkout, OR they entered a different email at checkout. Ask them what email they typed when adding the card.
                </div>
            ) : (
                <div className="space-y-2">
                    {customers.map((c, i) => (
                        <div
                            key={c.stripe_customer_id || i}
                            data-testid={`diag-customer-${c.stripe_customer_id}`}
                            className={`border p-2 ${c.is_canonical_in_db ? "border-emerald-700/40 bg-emerald-950/10" : "border-zinc-800 bg-zinc-900/30"}`}
                        >
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <code className="text-[10px] text-zinc-300 font-mono break-all">{c.stripe_customer_id}</code>
                                <div className="flex items-center gap-1.5">
                                    {c.is_canonical_in_db && (
                                        <span className="text-[9px] tracking-[0.15em] uppercase bg-emerald-900/40 text-emerald-300 border border-emerald-700/40 px-1.5 py-0.5">In our DB</span>
                                    )}
                                    {!c.is_canonical_in_db && (
                                        <button
                                            type="button"
                                            data-testid={`diag-link-${c.stripe_customer_id}`}
                                            onClick={() => forceLink(c.stripe_customer_id)}
                                            disabled={linking !== null}
                                            className="text-[9px] tracking-[0.15em] uppercase border border-amber-700/60 bg-amber-900/30 text-amber-200 hover:bg-amber-700 hover:text-white px-2 py-0.5 disabled:opacity-50 inline-flex items-center gap-1"
                                            title="Force-link the MaxSC user to this Stripe customer"
                                        >
                                            {linking === c.stripe_customer_id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : null}
                                            Link this
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="text-[10px] text-zinc-500 mt-1">
                                {c.stripe_email} · {c.name || "(no name)"} · created {new Date(c.created).toLocaleDateString()}
                            </div>
                            <div className={`text-[11px] mt-1.5 ${c.attached_card_count > 0 ? "text-emerald-300" : "text-zinc-500"}`}>
                                {c.attached_card_count > 0 ? `✓ ${c.attached_card_count} card${c.attached_card_count === 1 ? "" : "s"} attached` : "✗ No cards"}
                                {c.default_payment_method && <span className="text-zinc-400 ml-2">(default set)</span>}
                            </div>
                            {(c.cards || []).map((card, ci) => (
                                <div key={card.pm_id || ci} className="text-[10px] text-zinc-400 mt-1 font-mono ml-3">
                                    • {card.brand || "?"} ••••{card.last4} exp {card.exp_month}/{String(card.exp_year || "").slice(-2)}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}



export default function SetupCardForClientButton({
    clientUserId,
    clientEmail,
    clientName,
    onChanged,
}) {
    const [busy, setBusy] = useState(null); // "open" | "email" | null
    const [pendingUrl, setPendingUrl] = useState(null);
    const [cardCount, setCardCount] = useState(null); // null=loading, else # saved cards
    const [refreshKey, setRefreshKey] = useState(0);
    const bumpCards = useCallback(() => setRefreshKey((k) => k + 1), []);

    const triggerOpen = async () => {
        // Mobile Safari + iOS Chrome BLOCK window.open() when the destination
        // URL is resolved after an await — even when the open() call itself
        // is synchronous. The new tab dies silently → button looks dead.
        // (Sachin 2026-02-17: production mobile coach-side report.) On mobile
        // we navigate the current tab instead; Stripe Checkout redirects back
        // to /coach/billing/setup-complete when the card is saved.
        const isMobile = typeof window !== "undefined"
            && window.matchMedia?.("(max-width: 767px)").matches;
        // Desktop only: open the synchronous "about:blank" so the browser
        // associates the new tab with the user click. NOTE: must NOT pass
        // "noopener" here — that makes window.open() return null by spec,
        // leaving the tab stranded on about:blank with no way to navigate it.
        const win = isMobile ? null : window.open("about:blank", "_blank");
        if (win) { try { win.opener = null; } catch (_e) { /* noop */ } }
        setBusy("open");
        setPendingUrl(null);
        try {
            const r = await axios.post(
                `${API}/coach/clients/${clientUserId}/setup-card`,
                { origin_url: window.location.origin, delivery: "open" },
                { withCredentials: true },
            );
            const url = r.data?.url;
            if (r.data?.simulated) {
                if (win && !win.closed) win.close();
                toast.success("Demo client already has a simulated card on file", {
                    description: r.data.message || "No Stripe setup needed.",
                });
                return;
            }
            if (!url) throw new Error("No Checkout URL returned");
            if (isMobile) {
                // Same-tab redirect. Coach lands back on /coach/billing/setup-complete
                // after Stripe Checkout finishes.
                toast.success("Opening Stripe Checkout…");
                window.location.href = url;
                return;
            }
            if (win && !win.closed) {
                win.location.href = url;
                toast.success("Stripe Checkout opened in a new tab", {
                    description: "Enter the card there; success returns to billing.",
                });
            } else {
                setPendingUrl(url);
                toast.warning("Popup blocked — open the link manually", {
                    description: "Click 'Open Checkout link' below.",
                });
            }
        } catch (e) {
            if (win && !win.closed) win.close();
            toast.error("Couldn't start card setup", { description: e?.response?.data?.detail || e.message });
        } finally {
            setBusy(null);
        }
    };

    const triggerEmail = async () => {
        setBusy("email");
        try {
            const r = await axios.post(
                `${API}/coach/clients/${clientUserId}/setup-card`,
                { origin_url: window.location.origin, delivery: "email" },
                { withCredentials: true },
            );
            if (r.data?.simulated) {
                toast.success("Demo client already has a simulated card on file", {
                    description: r.data.message || "No Stripe setup needed.",
                });
                return;
            }
            if (r.data.emailed) {
                toast.success(`Card-setup link emailed to ${clientEmail}`, {
                    description: "They'll see a one-tap CTA and Stripe-hosted card form.",
                });
            } else {
                toast.error("Couldn't email link", {
                    description: r.data.email_error || "Resend might be misconfigured.",
                });
            }
        } catch (e) {
            toast.error("Couldn't start card setup", { description: e?.response?.data?.detail });
        } finally {
            setBusy(null);
        }
    };

    const [diagnostic, setDiagnostic] = useState(null);
    const [diagnosticOpen, setDiagnosticOpen] = useState(false);

    const loadDiagnostic = useCallback(async () => {
        if (!clientUserId) return;
        setBusy("diagnose");
        try {
            const r = await axios.get(
                `${API}/coach/clients/${clientUserId}/stripe-customer-diagnostic`,
                { withCredentials: true },
            );
            setDiagnostic(r.data);
            setDiagnosticOpen(true);
        } catch (e) {
            toast.error("Diagnostic failed", { description: e?.response?.data?.detail || "Server error" });
        } finally { setBusy(null); }
    }, [clientUserId]);

    const triggerResync = async () => {
        if (!clientUserId) return;
        setBusy("resync");
        try {
            const r = await axios.post(
                `${API}/coach/clients/${clientUserId}/sync-card-on-file`,
                {}, { withCredentials: true },
            );
            const result = r.data?.result;
            // Translate the engine's machine-readable result into something
            // a coach actually understands.
            const message = {
                promoted: "Found a card on Stripe — promoted to default. Coach side now sees it.",
                cache_refreshed: "Card on Stripe confirmed — refreshed your view.",
                already_default: "Card already in good shape — nothing to fix.",
                relinked: "✓ Recovered! Found their card on a duplicate Stripe customer and re-linked. Card-on-file is now live.",
                no_card: "Stripe has customers for this email but none have a card attached. Click 'Diagnose' to see what's there.",
                no_customer: "No Stripe customer found for this client's email. Click 'Diagnose' to inspect what Stripe actually has.",
                error: r.data?.error || "Stripe error — check the logs.",
            }[result] || `Synced (${result}).`;
            if (result === "no_card" || result === "no_customer") {
                toast.error(message, {
                    duration: 8000,
                    action: { label: "Diagnose", onClick: () => loadDiagnostic() },
                });
            } else if (result === "error") {
                toast.error(message);
            } else if (result === "relinked") {
                toast.success(message, {
                    description: `${r.data?.old_customer_id || "old"} → ${r.data?.new_customer_id || "new"}`,
                    duration: 8000,
                });
            } else {
                toast.success(message);
            }
            bumpCards();
            onChanged?.();
        } catch (e) {
            toast.error("Re-sync failed", { description: e?.response?.data?.detail || "Server error" });
        } finally {
            setBusy(null);
        }
    };

    const hasCard = cardCount != null && cardCount > 0;
    const ctaLabel = hasCard ? "Add / replace card" : "Open here";

    return (
        <div
            data-testid="setup-card-on-file-panel"
            className="p-3.5 border border-zinc-800 bg-zinc-950/40"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-white mb-1.5 inline-flex items-center gap-1.5">
                        <CreditCard className="w-3.5 h-3.5 text-brand-red" /> Cards on file
                    </div>
                    <CardSelector
                        userId={clientUserId}
                        selectable={false}
                        refreshKey={refreshKey}
                        onLoaded={(list) => setCardCount(list.length)}
                    />
                    {cardCount === 0 && (
                        <div data-testid="card-on-file-status-absent" className="text-[11px] text-zinc-400 mt-1">
                            No card saved yet — set one up so future charges go through in one tap.
                        </div>
                    )}
                </div>
            </div>
            <div className="mt-2.5 grid grid-cols-2 gap-2">
                <button
                    type="button"
                    data-testid="coach-card-setup-open"
                    onClick={triggerOpen}
                    disabled={busy !== null}
                    className="text-[10px] tracking-[0.15em] uppercase border border-brand-red bg-brand-red/10 text-brand-red hover:bg-brand-red hover:text-white px-2.5 py-2 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                >
                    {busy === "open" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    {busy === "open" ? "…" : ctaLabel}
                </button>
                <button
                    type="button"
                    data-testid="coach-card-setup-email"
                    onClick={triggerEmail}
                    disabled={busy !== null || !clientEmail}
                    title={clientEmail ? `Email a Stripe-hosted setup link to ${clientEmail}` : "No email on file"}
                    className="text-[10px] tracking-[0.15em] uppercase border border-zinc-700 text-zinc-200 hover:border-brand-red hover:text-brand-red px-2.5 py-2 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                >
                    {busy === "email" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    {busy === "email" ? "…" : "Email link"}
                </button>
            </div>
            {/* Re-sync / ghost-customer recovery — full-width because this is
                the rescue button for the duplicate-Stripe-customer bug. If a
                parent insists they entered a card but we don't see it, click
                here to scan Stripe by email and re-link the right customer. */}
            <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                    type="button"
                    data-testid="coach-card-resync"
                    onClick={triggerResync}
                    disabled={busy !== null}
                    title="Scans Stripe by email AND parent's email, then re-links the customer that actually holds the card."
                    className="text-[10px] tracking-[0.18em] uppercase border border-amber-700/60 bg-amber-900/20 text-amber-200 hover:bg-amber-800/40 hover:border-amber-600 px-2.5 py-2.5 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                >
                    {busy === "resync" ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                        <RefreshCw className="w-3 h-3" />
                    )}
                    {busy === "resync" ? "Scanning…" : "Re-sync from Stripe"}
                </button>
                <button
                    type="button"
                    data-testid="coach-card-diagnose"
                    onClick={loadDiagnostic}
                    disabled={busy !== null}
                    title="Show EVERY Stripe customer matching this client's email (or parent's email) and what cards are attached to each."
                    className="text-[10px] tracking-[0.18em] uppercase border border-zinc-700 bg-zinc-900/40 text-zinc-300 hover:border-amber-600 hover:text-amber-200 px-2.5 py-2.5 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                >
                    {busy === "diagnose" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                    {busy === "diagnose" ? "Loading…" : "Diagnose"}
                </button>
            </div>
            {diagnosticOpen && diagnostic && (
                <DiagnosticPanel
                    data={diagnostic}
                    userId={clientUserId}
                    onClose={() => setDiagnosticOpen(false)}
                    onChanged={() => { bumpCards(); onChanged?.(); }}
                />
            )}
            {pendingUrl && (
                <div className="mt-2.5 p-2 border border-amber-700/60 bg-amber-900/20 rounded" data-testid="card-setup-popup-fallback">
                    <p className="text-[10px] text-amber-200 mb-1.5">Popup was blocked. Click below to open Stripe Checkout in this tab:</p>
                    <a
                        href={pendingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase text-amber-100 underline hover:text-white"
                    >
                        Open Checkout link →
                    </a>
                </div>
            )}
            {hasCard && (
                <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
                    When you run a <span className="text-white">Charge</span> or pack, you'll pick which of these cards to use. To add another, click <span className="text-white">Add / replace card</span>.
                </p>
            )}
        </div>
    );
}
